import { useCallback, useEffect, useState } from 'react';
import {
  createInventoryItemCategory,
  listInventoryItemCategories
} from '../Api/Inventory.api';
import type { InventoryItemCategory } from '../Types';

export const useInventoryCategories = (
  sessionToken: string | null,
  branchId: string | undefined,
  enabled: boolean
) => {
  const [categories, setCategories] = useState<InventoryItemCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!enabled || !sessionToken || !branchId) {
      setCategories([]);
      return;
    }

    setLoading(true);
    try {
      const rows = await listInventoryItemCategories({
        accessToken: sessionToken,
        branchId
      });
      setCategories(rows);
    } catch {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [branchId, enabled, sessionToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const createCategory = useCallback(
    async (name: string) => {
      if (!sessionToken || !branchId) {
        throw new Error('Session required');
      }

      setCreating(true);
      try {
        const created = await createInventoryItemCategory(name, {
          accessToken: sessionToken,
          branchId
        });
        setCategories(prev => {
          const without = prev.filter(c => c.id !== created.id);
          return [...without, created].sort((a, b) => {
            const order = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
            if (order !== 0) return order;
            return a.name.localeCompare(b.name);
          });
        });
        return created;
      } finally {
        setCreating(false);
      }
    },
    [branchId, sessionToken]
  );

  return {
    categories,
    loading,
    creating,
    reload: load,
    createCategory
  };
};
